/**
 * AnnotationPanel - Display AI-generated annotations
 */

import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Lightbulb, 
  AlertCircle,
  CheckCircle,
  FileSearch,
  ExternalLink,
  Search,
  HelpCircle,
} from 'lucide-react';
import type { AIAnnotation, AnnotationNextAction, Signal } from './types';

interface AnnotationPanelProps {
  annotations: AIAnnotation[];
  signals: Signal[];
  selectedPeriod: string | null;
  isLoading?: boolean;
  error?: string | null;
  aiAvailable?: boolean;
}

const impactColors: Record<string, string> = {
  HIGH: 'bg-destructive/10 text-destructive border-destructive/20',
  MEDIUM: 'bg-warning/10 text-warning-foreground border-warning/20',
  LOW: 'bg-muted text-muted-foreground border-muted-foreground/20',
};

const actionConfig: Record<AnnotationNextAction, { label: string; icon: React.ReactNode }> = {
  OPEN_TRANSACTIONS: { label: 'Öppna transaktioner', icon: <FileSearch className="h-3 w-3" /> },
  CREATE_SAMPLE: { label: 'Skapa urval', icon: <CheckCircle className="h-3 w-3" /> },
  ADD_EVIDENCE: { label: 'Lägg till revisionsbevis', icon: <ExternalLink className="h-3 w-3" /> },
  INVESTIGATE_FURTHER: { label: 'Undersök vidare', icon: <Search className="h-3 w-3" /> },
};

export function AnnotationPanel({ 
  annotations, 
  signals,
  selectedPeriod,
  isLoading,
  error,
  aiAvailable = true,
}: AnnotationPanelProps) {
  // Filter by selected period if set
  const filteredAnnotations = selectedPeriod
    ? annotations.filter(a => a.evidence.period === selectedPeriod)
    : annotations;
  
  // Group by period
  const grouped = filteredAnnotations.reduce((acc, a) => {
    const key = a.evidence.period;
    if (!acc[key]) acc[key] = [];
    acc[key].push(a);
    return acc;
  }, {} as Record<string, AIAnnotation[]>);
  
  // Sort periods descending
  const sortedPeriods = Object.keys(grouped).sort().reverse();
  
  // If AI not available, show fallback
  if (!aiAvailable) {
    const highSeveritySignals = signals.filter(s => s.severity === 'HIGH');
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          <p className="font-medium">AI-tjänsten är inte tillgänglig</p>
          <p className="text-sm text-muted-foreground mt-1">
            Visar endast deterministiska signaler.
          </p>
          {highSeveritySignals.length > 0 && (
            <ul className="mt-2 text-sm space-y-1">
              {highSeveritySignals.slice(0, 3).map((s, i) => (
                <li key={i} className="flex items-start gap-1">
                  <span className="text-destructive">•</span>
                  <span>{s.period}: {s.explanation}</span>
                </li>
              ))}
            </ul>
          )}
        </AlertDescription>
      </Alert>
    );
  }
  
  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
          <p className="text-sm text-muted-foreground mt-3">Genererar AI-insikter...</p>
        </div>
      </div>
    );
  }
  
  // Error state
  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }
  
  // Empty state
  if (filteredAnnotations.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <HelpCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p className="text-sm">
          {annotations.length === 0 
            ? 'Klicka "Generera" för att få AI-baserade insikter'
            : `Inga insikter för ${selectedPeriod}`
          }
        </p>
      </div>
    );
  }
  
  return (
    <ScrollArea className="h-[450px]">
      <div className="space-y-4 pr-4">
        {sortedPeriods.map((period) => (
          <div key={period}>
            <div className="text-xs font-medium text-muted-foreground mb-2 sticky top-0 bg-background py-1">
              {period}
            </div>
            <div className="space-y-2">
              {grouped[period].map((annotation) => (
                <Card 
                  key={annotation.id} 
                  className={`border ${impactColors[annotation.impact]}`}
                >
                  <CardContent className="p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <Lightbulb className="h-4 w-4 flex-shrink-0" />
                        <span className="font-medium text-sm truncate">
                          {annotation.title}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {annotation.is_hypothesis && (
                          <Badge variant="outline" className="text-xs">
                            Hypotes
                          </Badge>
                        )}
                        <Badge variant="secondary" className="text-xs">
                          {Math.round(annotation.confidence * 100)}%
                        </Badge>
                      </div>
                    </div>
                    
                    <p className="text-sm mt-2 text-foreground/80 leading-relaxed">
                      {annotation.message}
                    </p>
                    
                    {annotation.evidence.driving_accounts.length > 0 && (
                      <div className="text-xs text-muted-foreground mt-2">
                        <span className="font-medium">Drivande konton:</span>{' '}
                        {annotation.evidence.driving_accounts.join(', ')}
                      </div>
                    )}
                    
                    <Button
                      size="sm"
                      variant="ghost"
                      className="mt-2 h-7 text-xs gap-1"
                    >
                      {actionConfig[annotation.next_action]?.icon}
                      {actionConfig[annotation.next_action]?.label}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        ))}
      </div>
    </ScrollArea>
  );
}

/**
 * Compact annotation count for header
 */
export function AnnotationCount({ annotations }: { annotations: AIAnnotation[] }) {
  const highImpact = annotations.filter(a => a.impact === 'HIGH').length;
  
  if (annotations.length === 0) {
    return <Badge variant="outline">0</Badge>;
  }
  
  return (
    <Badge variant={highImpact > 0 ? 'destructive' : 'secondary'}>
      {annotations.length}
    </Badge>
  );
}
